import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { HiOutlineArchiveBox, HiOutlineBellSlash, HiOutlineCheck, HiOutlineChevronDown, HiOutlineChevronUp, HiOutlineEyeSlash } from "react-icons/hi2";
import { NotificationReferenceContent } from "@/components/notifications/notification-reference-content";
import { Button } from "@/components/ui";
import { ButtonGroup } from "@/components/ui/button-group";
import { formatDate, formatWeekdayLong } from "@/lib/format-data";
import { CONVERSATION_ALERT_TYPES } from "@/lib/notification-methods";
import { isTypeStack, stackSummary } from "@/lib/notification-stack";
import { cx } from "@/lib/tailwind-utils";
import { AlertType } from "@/types/enums/alert-type";
import { ProviderNotificationSummary } from "@/types/notification";
import { useCombinedNotifications } from "@/query/notifications/notifications";

/**
 * Compact timestamp for notification items. Unlike calculateRelativeDuration
 * (which returns durations like "3 days ago"), this returns anchor-style labels:
 * "Just now" / "5 mins ago" / "Yesterday" / "Monday" / "Jan 15" / "Jan 15, 2024"
 */
function formatRelativeTime(dateString: string | Date): string {
  const date = typeof dateString === "string" ? new Date(dateString) : dateString;
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffSeconds < 60) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes} min${diffMinutes === 1 ? "" : "s"} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return formatWeekdayLong(date);
  if (diffDays < 365) return formatDate(date, true, false);
  return formatDate(date);
}

/** "4 new messages", "3 alerts" — what a stacked row stands for. */
export function stackLabel(notification: ProviderNotificationSummary): string | null {
  const count = notification.messageCount ?? 1;
  if (count <= 1) return null;
  const noun = CONVERSATION_ALERT_TYPES.has(notification.alert.type) ? "messages" : "alerts";
  return `${count} ${notification.read ? "" : "new "}${noun}`;
}

/** The earlier notifications behind a stacked row, loaded when it is opened. */
function CombinedNotifications(t0) {
  const $ = _c(6);
  const {
    notificationId
  } = t0;
  const {
    data,
    isLoading,
    isError
  } = useCombinedNotifications(notificationId, true);
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="mt-2 space-y-2" aria-busy="true">{[1, 2].map(_temp)}</div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (isError || !data) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p className="mt-2 text-caption text-foreground-muted">Couldn’t load the earlier alerts.</p>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== data) {
    t1 = data.map(_temp2);
    $[2] = data;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = <ul className="mt-2 space-y-2 border-l border-border pl-3">{t1}</ul>;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function _temp2(earlier) {
  return <li key={earlier.id} className="flex items-start justify-between gap-3"><NotificationReferenceContent content={earlier.alert.content ?? "System notification"} className="min-w-0 flex-1 text-caption text-foreground-secondary" /><span className="whitespace-nowrap text-caption text-muted-foreground">{formatRelativeTime(earlier.createdDate)}</span></li>;
}
function _temp(i) {
  return <div key={i} className="h-4 animate-pulse rounded bg-muted" />;
}
interface NotificationItemProps {
  notification: ProviderNotificationSummary;
  quickActions: Array<{
    label: string;
    onClick: () => void;
  }>;
  onMarkAsRead: (ids: string[]) => void;
  onMarkAsUnread: (ids: string[]) => void;
  onArchive: (ids: string[]) => void;
  onMuteAlertType: (alertType: string) => void;
}
export function NotificationItem(t0) {
  const $ = _c(63);
  const {
    notification,
    quickActions,
    onMarkAsRead,
    onMarkAsUnread,
    onArchive,
    onMuteAlertType
  } = t0;
  const alert = notification.alert;
  const isUnread = !notification.read;
  let t1;
  if ($[0] !== notification) {
    t1 = isTypeStack(notification) ? stackSummary(notification) : null;
    $[0] = notification;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const summary = t1;
  let t2;
  if ($[2] !== notification || $[3] !== summary) {
    t2 = summary ? null : stackLabel(notification);
    $[2] = notification;
    $[3] = summary;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const stack = t2;
  const [expanded, setExpanded] = useState(false);
  const t3 = `${isUnread ? "Unread notification" : "Notification"}: ${summary ?? alert.content ?? "System notification"}`;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cx("group relative flex border-b border-border px-4 py-3 transition-colors hover:bg-hover focus-within:bg-hover outline-none");
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== alert.content || $[7] !== isUnread || $[8] !== summary) {
    t5 = <div className="min-w-0 flex-1">{summary ? <p className={cx("text-body leading-snug", isUnread ? "font-medium text-foreground" : "text-foreground-secondary")}>{summary}</p> : <NotificationReferenceContent content={alert.content ?? "System notification"} className={cx("text-body leading-snug", isUnread ? "font-medium text-foreground" : "text-foreground-secondary")} />}</div>;
    $[6] = alert.content;
    $[7] = isUnread;
    $[8] = summary;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== notification.createdDate) {
    t6 = formatRelativeTime(notification.createdDate);
    $[10] = notification.createdDate;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <span className="whitespace-nowrap text-caption text-muted-foreground">{t6}</span>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== isUnread) {
    t8 = isUnread && <div className="h-1.5 w-1.5 rounded-full bg-primary" />;
    $[14] = isUnread;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t7 || $[17] !== t8) {
    t9 = <div className="flex items-center gap-2 group-hover:invisible">{t7}{t8}</div>;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== alert.type || $[20] !== onMuteAlertType) {
    t10 = alert.type !== AlertType.TEAM_MESSAGE_RECEIVED && <Button variant="ghost" className="h-7 w-7 p-0" tooltip="Turn off this notification type" aria-label="Turn off this notification type" onClick={e => {
      e.stopPropagation();
      onMuteAlertType(alert.type);
    }} icon={<HiOutlineBellSlash className="h-4 w-4" />} />;
    $[19] = alert.type;
    $[20] = onMuteAlertType;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== notification.id || $[23] !== onArchive) {
    t11 = e_0 => {
      e_0.stopPropagation();
      onArchive([notification.id]);
    };
    $[22] = notification.id;
    $[23] = onArchive;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <HiOutlineArchiveBox className="h-4 w-4" />;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t11) {
    t13 = <Button variant="ghost" className="h-7 w-7 p-0" tooltip="Archive" aria-label="Archive notification" onClick={t11} icon={t12} />;
    $[26] = t11;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const t14 = isUnread ? "Mark as read" : "Mark as unread";
  const t15 = isUnread ? "Mark as read" : "Mark as unread";
  let t16;
  if ($[28] !== isUnread || $[29] !== notification.id || $[30] !== onMarkAsRead || $[31] !== onMarkAsUnread) {
    t16 = e_1 => {
      e_1.stopPropagation();
      if (isUnread) {
        onMarkAsRead([notification.id]);
      } else {
        onMarkAsUnread([notification.id]);
      }
    };
    $[28] = isUnread;
    $[29] = notification.id;
    $[30] = onMarkAsRead;
    $[31] = onMarkAsUnread;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== isUnread) {
    t17 = isUnread ? <HiOutlineCheck className="h-4 w-4" /> : <HiOutlineEyeSlash className="h-4 w-4" />;
    $[33] = isUnread;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t14 || $[36] !== t15 || $[37] !== t16 || $[38] !== t17) {
    t18 = <Button variant="ghost" className="h-7 w-7 p-0" tooltip={t14} aria-label={t15} onClick={t16} icon={t17} />;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t10 || $[41] !== t13 || $[42] !== t18) {
    t19 = <ButtonGroup className="absolute right-0 top-1/2 hidden -translate-y-1/2 rounded-md border border-border bg-background shadow-sm group-hover:flex group-focus-within:flex">{t10}{t13}{t18}</ButtonGroup>;
    $[40] = t10;
    $[41] = t13;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t19 || $[45] !== t9) {
    t20 = <div className="relative flex min-w-[5.5rem] shrink-0 items-center justify-end">{t9}{t19}</div>;
    $[44] = t19;
    $[45] = t9;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t20 || $[48] !== t5) {
    t21 = <div className="flex items-start justify-between gap-3">{t5}{t20}</div>;
    $[47] = t20;
    $[48] = t5;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== expanded || $[51] !== notification.id || $[52] !== stack) {
    t22 = stack && <><button type="button" aria-expanded={expanded} className="mt-1 flex items-center gap-1 text-caption-strong text-foreground-secondary hover:text-foreground" onClick={e_2 => {
        e_2.stopPropagation();
        setExpanded(_temp3);
      }}>{stack}{expanded ? <HiOutlineChevronUp className="h-3.5 w-3.5" /> : <HiOutlineChevronDown className="h-3.5 w-3.5" />}</button>{expanded && <CombinedNotifications notificationId={notification.id} />}</>;
    $[50] = expanded;
    $[51] = notification.id;
    $[52] = stack;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== quickActions) {
    t23 = quickActions.length > 0 && <div className="mt-2 flex flex-wrap gap-2">{quickActions.map(_temp4)}</div>;
    $[54] = quickActions;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t21 || $[57] !== t22 || $[58] !== t23) {
    t24 = <div className="min-w-0 flex-1">{t21}{t22}{t23}</div>;
    $[56] = t21;
    $[57] = t22;
    $[58] = t23;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== t24 || $[61] !== t3) {
    t25 = <div role="article" tabIndex={0} aria-label={t3} className={t4}>{t24}</div>;
    $[60] = t24;
    $[61] = t3;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  return t25;
}
function _temp4(action, idx) {
  return <button key={idx} aria-label={action.label} className="rounded-md border border-border bg-background px-3 py-1 text-caption-strong text-foreground-secondary transition-colors hover:bg-muted hover:text-foreground" onClick={e_3 => {
    e_3.stopPropagation();
    action.onClick();
  }}>{action.label}</button>;
}
function _temp3(open) {
  return !open;
}